import axios from 'axios';

// Set REACT_APP_API_URL in `frontend/.env` (baked in at build/start). Fallback: local Nest.
export const API_BASE_URL = "https://missoi-hms-core.onrender.com/api";

export const api = axios.create({
  baseURL: API_BASE_URL,
  headers: { 'Content-Type': 'application/json' },
});

api.interceptors.request.use((config) => {
  const token = localStorage.getItem('hms-access-token');
  if (token) {
    config.headers.Authorization = `Bearer ${token}`;
  }
  return config;
});

api.interceptors.response.use(
  (res) => res,
  (error) => {
    const status = error.response?.status;
    const data = error.response?.data;
    let message = 'Something went wrong';
    if (!error.response) {
      // Network / CORS / wrong API URL (e.g. production build still pointing at localhost)
      message =
        error.code === 'ERR_NETWORK' || error.message === 'Network Error'
          ? 'Cannot reach the server. If you are on the live site, the app must be built with REACT_APP_API_URL set to your HTTPS API (not localhost).'
          : error.message || message;
    } else if (Array.isArray(data?.message)) {
      message = data.message.join(', ');
    } else if (typeof data?.message === 'string') {
      message = data.message;
    } else if (typeof data?.error === 'string') {
      message = data.error;
    }
    const enriched = error;
    enriched.apiMessage = message;
    if (status === 401) {
      localStorage.removeItem('hms-access-token');
      localStorage.removeItem('hms-user');
      if (
        typeof window !== 'undefined' &&
        !window.location.pathname.startsWith('/login') &&
        !window.location.pathname.startsWith('/register') &&
        !window.location.pathname.startsWith('/register-hospital')
      ) {
        window.location.href = '/login';
      }
    }
    return Promise.reject(enriched);
  },
);

export function getApiErrorMessage(error) {
  return error?.apiMessage || error?.message || 'Something went wrong';
}

/**
 * List GCS objects under a prefix (JWT). GET /upload?path=...
 */
export async function fetchDocuments(path = 'documents') {
  const { data } = await api.get('/upload', { params: { path } });
  return data;
}

/**
 * Multipart upload (JWT). Field name must be `file`. Uses fetch so boundary is set correctly.
 */
export async function forgotPassword(email) {
  const { data } = await api.post('/auth/forgot-password', { email });
  return data;
}

export async function resetPassword({ email, token, password }) {
  const { data } = await api.post('/auth/reset-password', {
    email,
    token,
    password,
  });
  return data;
}

export async function uploadDocumentFile(file, path = 'documents') {
  const token = localStorage.getItem('hms-access-token');
  const formData = new FormData();
  formData.append('file', file);
  const url = `${API_BASE_URL}/upload?path=${encodeURIComponent(path)}`;
  const res = await fetch(url, {
    method: 'POST',
    headers: token ? { Authorization: `Bearer ${token}` } : {},
    body: formData,
  });
  let data = {};
  try {
    data = await res.json();
  } catch {
    /* ignore */
  }
  if (!res.ok) {
    const msg = Array.isArray(data?.message)
      ? data.message.join(', ')
      : data?.message || res.statusText || 'Upload failed';
    const err = new Error(msg);
    err.apiMessage = msg;
    err.response = { status: res.status, data };
    throw err;
  }
  return data;
}
